import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const categoryLabelMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  jilupian: '纪录片',
  duanpian: '短片',
  dongman: '动漫',
  zongyi: '综艺',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'dianying';
  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  const categoryVideos = videos
    .filter((v) => v.category === currentSlug)
    .sort((a, b) => {
      if (sortBy === 'latest') return b.year - a.year;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 亚洲精品视频`,
    description: currentCategory?.description || '',
    url: `https://beevpn.cn/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲精品视频',
      url: 'https://beevpn.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览', href: '/category/dianying' },
                  { label: currentCategory?.name || '' },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">{currentCategory?.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong>{categoryVideos.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'rating' as const, label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {categoryVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2 flex gap-1.5">
                      {video.isNew && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded">
                          新
                        </span>
                      )}
                      {video.isHot && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded">
                          热
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-foreground-950/70 text-background-50 text-[10px] rounded">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors text-sm truncate">
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between mt-2 text-xs text-foreground-400">
                      <span>{video.year} · {video.region}</span>
                      <span className="flex items-center gap-0.5 text-accent-500">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-[10px]"></i>
                        </span>
                        {video.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}